import React, { useMemo, useState } from 'react';
import { Flex, Tooltip } from 'antd';
import { faChevronLeft, faChevronRight, faPlay, faShare } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { StudyClient } from 'jf/api';
import { DevExHeader } from 'jf/common/DevExHeader';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { CopyLinkPopover } from 'jf/components/CopyLinkPopover';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExLottie } from 'jf/components/DevExLottie';
import { DevExSkeleton } from 'jf/components/DevExSkeleton';
import { DevExSteps } from 'jf/components/DevExSteps';
import { EditableText } from 'jf/components/EditableText';
import SavedCloud from 'jf/devex/lottie/SavedCloud.json';
import SavingCloud from 'jf/devex/lottie/SavingCloud.json';
import { useClientFlags } from 'jf/utils/useClientFlags';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { useCompleteTargetRespondentsStep } from './targetRespondents/useCompleteTargetRespondentsStep';
import { STUDY_BUILDER_STEPS, StudyBuilderStep, useStudyBuilderState } from './StudyBuilderState';
import { useStudyUpdater } from './useStudyUpdater';

const STUDY_BUILDER_STEP_LABELS: { [key in StudyBuilderStep]: string } = {
  'choose-study': 'Choose base survey',
  'customize-study': 'Customize survey',
  'target-respondents': 'Select recipients',
  'publish-study': 'Review and publish',
  'schedule-study': 'Schedule',
};

export const StudyBuilderHeader: React.FC = () => {
  const navigate = useNavigate();
  const theme = useDevExTheme();
  const state = useStudyBuilderState();
  const studyUpdater = useStudyUpdater(state.studyRef);
  const completeTargetRespondentsStep = useCompleteTargetRespondentsStep();

  const [nextLoading, setNextLoading] = useState(false);

  const { data: study } = useClientQuery(StudyClient.getStudy, { studyRef: state.studyRef });
  const flags = useClientFlags();
  let steps = [...STUDY_BUILDER_STEPS];
  const flagsToRemove = [
    { flag: flags.slackTargeting, step: 'target-respondents' },
    { flag: flags.scheduledCloseReminders, step: 'schedule-study' },
  ];
  flagsToRemove.forEach(({ flag, step }) => {
    if (!flag) {
      const stepIndex = steps.findIndex((s) => s === step);
      steps.splice(stepIndex, 1);
    }
  });
  // removes 'schedule-study' step if scheduledCloseDate is null (when survey existed before scheduling was added)
  if (!state.targetingEnabled) {
    const stepIndex = steps.findIndex((s) => s === 'schedule-study');
    if (stepIndex !== -1) {
      steps.splice(stepIndex, 1);
    }
  }

  const stepIndex = steps.findIndex((key) => key === state.step);
  const nextStep = steps[stepIndex + 1];
  const lastStep = steps[stepIndex - 1];

  const completionCallbacks: { [key in StudyBuilderStep]?: () => Promise<void> } = {
    'target-respondents': completeTargetRespondentsStep,
  };

  const onNext = async () => {
    const complete = completionCallbacks[state.step];
    if (complete) {
      setNextLoading(true);
      await complete();
      setNextLoading(false);
    }
    state.update({ step: nextStep });
  };

  const onPreview = () => {
    trackEvent('survey-editor:preview:start', { surveyRef: state.studyRef });
    navigate(`/study/${state.studyRef}`);
  };

  const nextDisabledTooltip = useMemo(() => {
    if (state.step === 'customize-study' && !state.prompts.length) {
      return 'You must add a question to continue.';
    }
    if (state.step === 'customize-study' && state.studyTeams.length === 0) {
      return 'You must select at least one team to continue.';
    }
    if (
      state.step === 'target-respondents' &&
      state.targetingEnabled &&
      !state.slackTargets.length &&
      !state.msteamsTargets.length
    ) {
      return 'You must select at least one recipient to continue.';
    }
  }, [state]);

  return (
    <DevExHeader
      left={
        <Flex gap={theme.variable.spacing.md} align="center">
          {stepIndex > 1 ? (
            <DevExButton
              size="large"
              type="outline"
              icon={<FontAwesomeIcon icon={faChevronLeft} />}
              onClick={() => state.update({ step: lastStep })}
            >
              {STUDY_BUILDER_STEP_LABELS[lastStep]}
            </DevExButton>
          ) : (
            <DevExButton
              size="large"
              type="outline"
              onClick={() => {
                if (state.updatingStudy) {
                  window.alert('Please wait while your survey is being saved.');
                } else {
                  navigate('/studies');
                }
              }}
            >
              Back to surveys
            </DevExButton>
          )}

          {state.step === 'customize-study' && (
            <Flex
              gap={theme.variable.spacing.xs}
              style={{ color: theme.color.text.secondary }}
              align="center"
            >
              <DevExLottie
                key={state.updatingStudy ? 'saving' : 'saved'}
                data={state.updatingStudy ? SavingCloud : SavedCloud}
                loop={state.updatingStudy}
                colors={[theme.color.background.default, theme.color.text.secondary]}
                style={{ height: 24 }}
              />
              {state.updatingStudy ? 'Saving' : 'Saved'}
            </Flex>
          )}
        </Flex>
      }
      right={
        <Flex gap={theme.variable.spacing.md}>
          {!!state.studyRef && (
            <>
              <Tooltip
                title={
                  state.step !== 'publish-study' ? 'Preview study questions like a respondent.' : ''
                }
              >
                <DevExButton
                  type="outline"
                  size="large"
                  icon={<FontAwesomeIcon icon={faPlay} />}
                  onClick={onPreview}
                  loading={state.updatingStudy}
                >
                  {state.step === 'publish-study' ? 'Preview' : ''}
                </DevExButton>
              </Tooltip>
              <CopyLinkPopover
                title="Share Preview Link"
                description="Anyone with this link will be able to preview the study questions like a respondent."
                link={`${window.location.origin}/study/${state.studyRef}`}
              >
                <DevExButton size="large" type="outline" icon={<FontAwesomeIcon icon={faShare} />}>
                  Share preview
                </DevExButton>
              </CopyLinkPopover>{' '}
            </>
          )}

          {!!state.studyRef && !!nextStep && (
            <Tooltip title={nextDisabledTooltip}>
              <DevExButton
                size="large"
                type="primary"
                icon={<FontAwesomeIcon icon={faChevronRight} />}
                iconPosition="end"
                onClick={onNext}
                loading={nextLoading}
                disabled={!!nextDisabledTooltip}
                style={{ cursor: nextDisabledTooltip ? 'not-allowed' : undefined }}
              >
                {STUDY_BUILDER_STEP_LABELS[nextStep]}
              </DevExButton>
            </Tooltip>
          )}
        </Flex>
      }
    >
      <Flex
        vertical
        align="center"
        gap={theme.variable.spacing.lg}
        style={{ lineHeight: theme.variable.lineHeight }}
      >
        {state.studyRef ? (
          study ? (
            <EditableText
              value={state.name}
              onChange={(value) => {
                state.update({ name: value });
                studyUpdater.update({ name: value });
              }}
              placeholder="Add name"
              size="large"
              inputStyle={{ minWidth: '240px' }}
              dataCy="study-name"
            />
          ) : (
            <Flex justify="center">
              <DevExSkeleton width={360} fontSize="xl" />
            </Flex>
          )
        ) : (
          <div>Create DevEx survey</div>
        )}

        <div style={{ fontSize: 'initial', fontWeight: 'initial' }}>
          <DevExSteps
            labels={steps.map((step) => STUDY_BUILDER_STEP_LABELS[step])}
            activeIndex={steps.findIndex((key) => key === state.step)}
          />
        </div>
      </Flex>
    </DevExHeader>
  );
};
